import type {
  IngestChannel,
  IngestChannelState,
  IngestRunSummary,
  IssueJobFailureCode,
  IssueJobRunStatus,
  StaleNotifyResult,
} from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIngestStatus, useIngestWrite } from '../../hooks/useIssueIngest.js';
import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { useBacklogSync, useStaleNotify } from '../../hooks/useIssueTodo.js';
import { formatIssueTime } from '../../lib/issue-mgmt-format.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 取込みチャンネルの状態の文言キーと札の色。
 * @graph-connects none
 */
const CHANNEL_STATES: Record<
  IngestChannelState,
  { labelKey: string; variant: 'success' | 'warning' | 'error' | 'default' }
> = {
  ingesting: { labelKey: 'issueMgmt.ingest.stateIngesting', variant: 'success' },
  awaiting_invite: { labelKey: 'issueMgmt.ingest.stateAwaitingInvite', variant: 'warning' },
  stopped: { labelKey: 'issueMgmt.ingest.stateStopped', variant: 'error' },
  disabled: { labelKey: 'issueMgmt.ingest.stateDisabled', variant: 'default' },
};

/**
 * 定期処理の失敗の区分の文言キー。
 * @graph-connects none
 */
export const JOB_FAILURE_LABEL_KEYS: Record<IssueJobFailureCode, string> = {
  auth_failed: 'issueMgmt.jobs.failure.authFailed',
  config_missing: 'issueMgmt.jobs.failure.configMissing',
  not_in_channel: 'issueMgmt.jobs.failure.notInChannel',
  rate_limited: 'issueMgmt.jobs.failure.rateLimited',
  timeout: 'issueMgmt.jobs.failure.timeout',
  api_error: 'issueMgmt.jobs.failure.apiError',
  parse_failed: 'issueMgmt.jobs.failure.parseFailed',
  write_failed: 'issueMgmt.jobs.failure.writeFailed',
  unknown: 'issueMgmt.jobs.failure.unknown',
};

/**
 * 最後の取込みの結果の文言キー（{{at}} 時刻・{{n}} 振り分けに出した声の数）。
 * @graph-connects none
 */
const INGEST_RUN_KEYS: Record<IngestRunSummary['status'], string> = {
  running: 'issueMgmt.jobs.ingestRunning',
  success: 'issueMgmt.jobs.ingestSuccess',
  partial: 'issueMgmt.jobs.ingestPartial',
  failed: 'issueMgmt.jobs.ingestFailed',
  aborted: 'issueMgmt.jobs.ingestAborted',
};

/**
 * 最後の Backlog 同期の結果の文言キー（{{at}} 時刻・{{n}} 写した案件の数）。
 * @graph-connects none
 */
const BACKLOG_RUN_KEYS: Record<IssueJobRunStatus, string> = {
  success: 'issueMgmt.jobs.backlogSuccess',
  partial: 'issueMgmt.jobs.backlogPartial',
  failed: 'issueMgmt.jobs.backlogFailed',
};

/**
 * 最後の滞留通知の結果の文言キー（{{at}} 時刻・{{n}} 滞留の数）。
 * @graph-connects none
 */
const STALE_RESULT_KEYS: Record<StaleNotifyResult, string> = {
  sent: 'issueMgmt.jobs.staleSent',
  nothing: 'issueMgmt.jobs.staleNothing',
  failed: 'issueMgmt.jobs.staleFailed',
};

/**
 * 取込みチャンネル1行（名前・状態・最終取込み・招待した／止める／再開する）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 取込みチャンネル1つの状態と最終取込みを見せ、招待待ち・止まったチャンネルは招待したで再開し、人の判断で止める・再開する
 * @graph-connects useIngestWrite [calls] markChannelInvited・setChannelEnabled
 */
function ChannelRow({
  channel,
  canEdit,
  now,
}: {
  channel: IngestChannel;
  canEdit: boolean;
  now: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const invited = useIngestWrite('markChannelInvited');
  const setEnabled = useIngestWrite('setChannelEnabled');
  const busy = invited.isPending || setEnabled.isPending;
  const state = CHANNEL_STATES[channel.state];
  const needsInvite = channel.state === 'awaiting_invite' || channel.state === 'stopped';
  const toggle = (enabled: boolean) => {
    setEnabled.mutate(
      { channelId: channel.channelId, enabled },
      {
        onSuccess: () => {
          toast.notifySaved(
            t(enabled ? 'issueMgmt.ingest.resumed' : 'issueMgmt.ingest.disabled', {
              channel: channel.channelName,
            }),
          );
        },
        onError: toast.notifyError,
      },
    );
  };
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
      <span className="font-semibold text-[var(--color-text-primary)]">#{channel.channelName}</span>
      <Badge variant={state.variant}>{t(state.labelKey)}</Badge>
      {channel.state === 'ingesting' && (
        <span className="text-xs text-[var(--color-text-muted)]">
          {channel.lastIngestedAt === null
            ? t('issueMgmt.ingest.notYet')
            : t('issueMgmt.ingest.lastIngested', {
                at: formatIssueTime(channel.lastIngestedAt, null, now) ?? '',
              })}
        </span>
      )}
      {needsInvite && (
        <span className="text-xs text-[var(--color-text-secondary)]">
          {channel.failure !== null && channel.failure.code !== 'not_in_channel'
            ? t(JOB_FAILURE_LABEL_KEYS[channel.failure.code])
            : t('issueMgmt.ingest.inviteHint')}
        </span>
      )}
      {canEdit && (
        <span className="ml-auto flex items-center gap-2">
          {needsInvite && (
            <Button
              size="sm"
              loading={invited.isPending}
              disabled={busy}
              onClick={() => {
                invited.mutate(
                  { channelId: channel.channelId },
                  {
                    onSuccess: () => {
                      toast.notifySaved(
                        t('issueMgmt.ingest.invited', { channel: channel.channelName }),
                      );
                    },
                    onError: toast.notifyError,
                  },
                );
              }}
            >
              {t('issueMgmt.ingest.markInvited')}
            </Button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              toggle(!channel.enabled);
            }}
            className="text-xs text-[var(--color-text-muted)] hover:underline disabled:opacity-60"
          >
            {channel.enabled ? t('issueMgmt.ingest.disable') : t('issueMgmt.ingest.resume')}
          </button>
        </span>
      )}
    </li>
  );
}

/**
 * 設定タブの「取込みチャンネル」（状態の一覧と追加）。チャンネル名か ID を入れて追加し、ai-fassy がまだ保存していなければ招待待ちになる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 声を取り込む Slack チャンネルの状態を一覧し、チャンネルを追加し、止まったチャンネルを招待したで再開する
 * @graph-connects useIngestStatus [calls] 取込みチャンネルの状態
 * @graph-connects useIngestWrite [calls] addChannel
 * @graph-connects ChannelRow [calls] チャンネル1行
 */
export function IngestChannelsSetting({ canEdit, now }: { canEdit: boolean; now: Date }) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const status = useIngestStatus();
  const add = useIngestWrite('addChannel');
  const [channel, setChannel] = useState('');
  const channels = status.data?.channels ?? [];

  const submit = () => {
    const value = channel.trim();
    if (value === '') return;
    add.mutate(
      { channel: value },
      {
        onSuccess: (added) => {
          setChannel('');
          toast.notifySaved(
            t('issueMgmt.ingest.added', { channel: added.channelName }),
            added.state === 'awaiting_invite' ? { detail: t('issueMgmt.ingest.inviteHint') } : {},
          );
        },
        onError: toast.notifyError,
      },
    );
  };

  return (
    <div>
      {status.isError ? (
        <p className="text-sm text-[var(--color-error)]">{t('issueMgmt.common.loadError')}</p>
      ) : status.data === undefined ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('issueMgmt.common.loading')}</p>
      ) : channels.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('issueMgmt.ingest.noChannels')}</p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)]">
          {channels.map((c) => (
            <ChannelRow key={c.channelId} channel={c} canEdit={canEdit} now={now} />
          ))}
        </ul>
      )}
      {canEdit && (
        <form
          className="mt-2 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Input
            aria-label={t('issueMgmt.ingest.addLabel')}
            placeholder={t('issueMgmt.ingest.addPlaceholder')}
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
            }}
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            loading={add.isPending}
            disabled={channel.trim() === ''}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t('issueMgmt.ingest.add')}
          </Button>
        </form>
      )}
    </div>
  );
}

/**
 * 定期処理の1行（名前・最後の実行・今すぐ動かすボタン）。
 * @graph-connects none
 */
function JobLine({
  name,
  last,
  failed,
  action,
}: {
  name: string;
  last: string;
  failed: boolean;
  action: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
      <span className="w-28 shrink-0 font-semibold text-[var(--color-text-primary)]">{name}</span>
      <span
        className={`min-w-0 flex-1 text-xs ${failed ? 'text-[var(--color-error)]' : 'text-[var(--color-text-secondary)]'}`}
      >
        {last}
      </span>
      {action}
    </li>
  );
}

/**
 * 設定タブの「定期処理」（声の取込み・Backlog 同期・滞留通知）。それぞれ最後の実行と結果を出し、今すぐ動かせる。
 * 声の取込みの下に、取込みが読む Slack の保存（ai-fassy）の最新時刻を出し、24時間より古ければ止まっていると出す。
 * 取込みは裏で動き、終わるまで状況を読み直して結果を出す。Backlog 同期は終わるまで待って結果をトーストで出す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 声の取込み・Backlog 同期・滞留通知の最後の実行と結果と、Slack の保存（ai-fassy）の最新時刻を見せ、今すぐ1回動かす
 * @graph-connects useIngestStatus [calls] 最後の取込み
 * @graph-connects useBacklogSync [calls] 最後の Backlog 同期
 * @graph-connects useStaleNotify [calls] 最後の滞留通知
 * @graph-connects useIngestWrite [calls] runNow
 * @graph-connects useIssueMgmtWrite [calls] syncBacklog・notifyStaleNow
 */
export function JobsSetting({ canEdit, now }: { canEdit: boolean; now: Date }) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const [awaitRunId, setAwaitRunId] = useState<string | null>(null);
  const ingest = useIngestStatus(awaitRunId);
  const backlog = useBacklogSync();
  const stale = useStaleNotify();
  const runNow = useIngestWrite('runNow');
  const syncBacklog = useIssueMgmtWrite('syncBacklog');
  const notifyStale = useIssueMgmtWrite('notifyStaleNow');
  const dash = t('issueMgmt.common.dash');
  const at = (iso: string | null) =>
    iso === null ? dash : (formatIssueTime(iso, null, now) ?? dash);
  const failureText = (code: IssueJobFailureCode | null) =>
    code === null ? '' : `（${t(JOB_FAILURE_LABEL_KEYS[code])}）`;

  const ingestRun = ingest.data?.lastRun ?? null;
  // 受け付けた実行が最後の取込みに出てくるまでも、実行中として扱う（押し直させない）
  const ingestRunning =
    ingestRun?.status === 'running' || (awaitRunId !== null && ingestRun?.runId !== awaitRunId);
  const ingestText =
    ingestRun === null
      ? t('issueMgmt.jobs.never')
      : t(INGEST_RUN_KEYS[ingestRun.status], {
          at: at(ingestRun.finishedAt ?? ingestRun.startedAt),
          n: String(ingestRun.processedCount ?? 0),
        }) + failureText(ingestRun.failureCode);

  const archive = ingest.data?.slackArchive ?? null;
  const archiveText =
    archive === null
      ? dash
      : archive.stale
        ? t('issueMgmt.jobs.archiveStopped', { at: at(archive.latestSavedAt) })
        : t('issueMgmt.jobs.archiveLatest', { at: at(archive.latestSavedAt) });

  const backlogRun = backlog.data?.lastRun ?? null;
  const backlogText =
    backlogRun === null
      ? t('issueMgmt.jobs.never')
      : t(BACKLOG_RUN_KEYS[backlogRun.runStatus], {
          at: at(backlogRun.finishedAt),
          n: String(backlogRun.processedCount),
        }) + failureText(backlogRun.failureCode);

  const staleRun = stale.data?.lastRun ?? null;
  const staleFailedAt = stale.data?.lastFailedAt ?? null;
  const staleText =
    staleRun === null
      ? t('issueMgmt.jobs.never')
      : t(STALE_RESULT_KEYS[staleRun.result], {
          at: at(staleRun.at),
          n: String(staleRun.staleCount),
        });

  return (
    <ul className="divide-y divide-[var(--color-border)]">
      <JobLine
        name={t('issueMgmt.jobs.ingestName')}
        last={ingestText}
        failed={
          ingestRun !== null && ingestRun.status !== 'success' && ingestRun.status !== 'running'
        }
        action={
          canEdit && (
            <Button
              variant="secondary"
              size="sm"
              loading={runNow.isPending || ingestRunning}
              disabled={ingestRunning}
              onClick={() => {
                runNow.mutate(undefined, {
                  onSuccess: (accepted) => {
                    setAwaitRunId(accepted.runId);
                    toast.notifySaved(t('issueMgmt.jobs.ingestStarted'), {
                      detail: t('issueMgmt.jobs.ingestStartedDetail'),
                    });
                  },
                  onError: toast.notifyError,
                });
              }}
            >
              {ingestRunning ? t('issueMgmt.jobs.running') : t('issueMgmt.jobs.runNow')}
            </Button>
          )
        }
      />
      <JobLine
        name={t('issueMgmt.jobs.archiveName')}
        last={archiveText}
        failed={archive?.stale === true}
        action={null}
      />
      <JobLine
        name={t('issueMgmt.jobs.backlogName')}
        last={backlogText}
        failed={backlogRun !== null && backlogRun.runStatus !== 'success'}
        action={
          canEdit && (
            <Button
              variant="secondary"
              size="sm"
              loading={syncBacklog.isPending}
              disabled={backlog.data?.running === true}
              onClick={() => {
                syncBacklog.mutate(undefined, {
                  onSuccess: (result) => {
                    if (result.outcome === 'already_running') {
                      toast.notifySaved(t('issueMgmt.jobs.alreadyRunning'));
                      return;
                    }
                    toast.notifySaved(
                      t(BACKLOG_RUN_KEYS[result.runStatus], {
                        at: t('issueMgmt.jobs.justNow'),
                        n: String(result.processedCount),
                      }),
                      result.failureCode === null
                        ? {}
                        : { detail: t(JOB_FAILURE_LABEL_KEYS[result.failureCode]) },
                    );
                  },
                  onError: toast.notifyError,
                });
              }}
            >
              {backlog.data?.running === true
                ? t('issueMgmt.jobs.running')
                : t('issueMgmt.jobs.runNow')}
            </Button>
          )
        }
      />
      <JobLine
        name={t('issueMgmt.jobs.staleName')}
        last={
          staleFailedAt === null
            ? staleText
            : `${staleText}・${t('issueMgmt.jobs.staleFailedAt', { at: at(staleFailedAt) })}`
        }
        failed={staleRun?.result === 'failed' || staleFailedAt !== null}
        action={
          canEdit && (
            <Button
              variant="secondary"
              size="sm"
              loading={notifyStale.isPending}
              onClick={() => {
                notifyStale.mutate(undefined, {
                  onSuccess: (result) => {
                    toast.notifySaved(
                      t(STALE_RESULT_KEYS[result.result], {
                        at: t('issueMgmt.jobs.justNow'),
                        n: String(result.staleCount),
                      }),
                    );
                  },
                  onError: toast.notifyError,
                });
              }}
            >
              {t('issueMgmt.jobs.sendNow')}
            </Button>
          )
        }
      />
    </ul>
  );
}

/**
 * 設定タブの「月1回の点検」。前回の結果を出し、今月の点検を始めるとやることの最下段を点検として開く。
 * 始めた点検が終わっていなければ「点検を続ける」にする。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 月1回の点検の前回の結果（本当は声だった数・伏せ字漏れの数）を見せ、今月の点検を始めてやることで見直す
 * @graph-connects useIngestStatus [calls] 前回の点検
 * @graph-connects useIngestWrite [calls] startInspection
 */
export function InspectionSetting({ canEdit, now }: { canEdit: boolean; now: Date }) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const navigate = useNavigate();
  const status = useIngestStatus();
  const start = useIngestWrite('startInspection');
  const latest = status.data?.latestInspection ?? null;
  const dash = t('issueMgmt.common.dash');
  const unfinished = latest !== null && latest.finishedAt === null;
  const open = () => {
    void navigate({ to: '/issue-mgmt/todo', search: { panel: 'inspection' } });
  };
  return (
    <div className="space-y-2">
      <p className="text-sm text-[var(--color-text-secondary)]">
        {latest === null
          ? t('issueMgmt.inspection.neverRun')
          : latest.finishedAt === null
            ? t('issueMgmt.inspection.inProgress', { month: latest.targetMonth })
            : t('issueMgmt.inspection.last', {
                month: latest.targetMonth,
                at: formatIssueTime(latest.finishedAt, null, now) ?? dash,
                real: latest.realVoiceCount === null ? dash : String(latest.realVoiceCount),
                leak: latest.leakCount === null ? dash : String(latest.leakCount),
              })}
      </p>
      {canEdit &&
        (unfinished ? (
          <Button variant="secondary" size="sm" onClick={open}>
            {t('issueMgmt.inspection.continue')}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            loading={start.isPending}
            onClick={() => {
              start.mutate(undefined, {
                onSuccess: () => {
                  toast.notifySaved(t('issueMgmt.inspection.started'));
                  open();
                },
                onError: toast.notifyError,
              });
            }}
          >
            {t('issueMgmt.inspection.start')}
          </Button>
        ))}
    </div>
  );
}
